import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ReactFlow, ReactFlowProvider, Background, BackgroundVariant, Controls, MiniMap,
  useNodesState, useEdgesState, addEdge, Handle, Position, useReactFlow, MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Save, Copy, Trash2, Undo2, Redo2, Maximize2, Play, Power, Loader2,
  Upload, Download, Zap, GitBranch, PlayCircle, X, LayoutTemplate, MessageSquare,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { BLOCKS, KIND_COLOR, KIND_OF, getSettingsSchema, TEMPLATES } from "@/lib/workflowBlocks";

const KIND_ICON = { trigger: Zap, action: PlayCircle, condition: GitBranch };

function BlockNode({ data, selected }) {
  const c = KIND_COLOR[data.kind] || KIND_COLOR.action;
  const Icon = KIND_ICON[data.kind] || PlayCircle;
  const isTrigger = data.kind === "trigger";
  const isCond = data.kind === "condition";
  return (
    <div title={data.label} style={{ background: c.bg, borderColor: c.border, color: c.text }}
      className={`rounded-xl border-2 px-3 py-2.5 min-w-[180px] shadow-sm ${selected ? "ring-2 ring-offset-1" : ""}`}>
      {!isTrigger && <Handle type="target" position={Position.Left} style={{ background: c.dot, width: 10, height: 10 }} />}
      <div className="flex items-center gap-2">
        <span className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: c.dot }}><Icon className="w-3.5 h-3.5 text-white" /></span>
        <div className="text-xs font-semibold leading-tight">{data.label}</div>
      </div>
      {isCond ? (
        <>
          <Handle id="true" type="source" position={Position.Right} style={{ top: "35%", background: "#16a34a", width: 10, height: 10 }} />
          <Handle id="false" type="source" position={Position.Right} style={{ top: "70%", background: "#dc2626", width: 10, height: 10 }} />
          <div className="absolute -right-8 text-[9px] font-bold" style={{ top: "26%", color: "#16a34a" }}>yes</div>
          <div className="absolute -right-7 text-[9px] font-bold" style={{ top: "62%", color: "#dc2626" }}>no</div>
        </>
      ) : (
        <Handle type="source" position={Position.Right} style={{ background: c.dot, width: 10, height: 10 }} />
      )}
    </div>
  );
}

const nodeTypes = { block: BlockNode };

function Palette() {
  const onDragStart = (e, type) => { e.dataTransfer.setData("application/reactflow", type); e.dataTransfer.effectAllowed = "move"; };
  const Section = ({ title, kind }) => (
    <div className="mb-4" data-testid={`palette-${kind}`}>
      <div className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: KIND_COLOR[kind].border }}>{title}</div>
      <div className="space-y-1.5">
        {BLOCKS[kind].map((b) => {
          const c = KIND_COLOR[kind];
          return (
            <div key={b.type} draggable onDragStart={(e) => onDragStart(e, b.type)}
              className="text-xs px-2.5 py-2 rounded-lg border cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow flex items-center gap-2"
              style={{ background: c.bg, borderColor: c.border, color: c.text }} data-testid={`palette-block-${b.type}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.dot }} />{b.label}
            </div>
          );
        })}
      </div>
    </div>
  );
  return (
    <div className="w-56 shrink-0 border-r bg-white dark:bg-[#0f0d24] overflow-y-auto p-3">
      <div className="text-xs text-slate-400 mb-3">Drag blocks onto the canvas</div>
      <Section title="Triggers" kind="trigger" />
      <Section title="Actions" kind="action" />
      <Section title="Conditions" kind="condition" />
    </div>
  );
}

const LEAD_FIELDS = [
  { v: "name", l: "Lead name" }, { v: "interest", l: "Interest / product" }, { v: "city", l: "City" },
  { v: "email", l: "Email" }, { v: "mobile", l: "Mobile" }, { v: "score", l: "Score" },
  { v: "category", l: "Category" }, { v: "status", l: "Status" }, { v: "language", l: "Language" },
];

const SAMPLE_LEAD = {
  name: "Rahul Sharma", interest: "ERP Software", city: "Mumbai", email: "rahul@example.com",
  mobile: "9876543210", score: "85", category: "HOT", status: "Qualified", language: "hi-IN",
};

function fillTemplate(body, varMap) {
  return (body || "").replace(/{{(\d+)}}/g, (_, n) => {
    const field = (varMap || [])[Number(n) - 1];
    return field ? (SAMPLE_LEAD[field] ?? `{{${n}}}`) : `{{${n}}}`;
  });
}

function WaTemplatePicker({ s, set }) {
  const [templates, setTemplates] = React.useState(null);
  const [err, setErr] = React.useState("");
  React.useEffect(() => {
    api.get("/integrations/whatsapp/templates").then(({ data }) => {
      if (data.ok) setTemplates(data.templates);
      else { setTemplates([]); setErr(
        data.reason === "no_waba_id" ? "Add your WABA ID in Integrations → WhatsApp → Configure to load templates."
        : data.reason === "whatsapp_not_configured" ? "Connect WhatsApp in Integrations first (token + WABA ID)."
        : (data.detail || data.reason || "Could not load templates")); }
    }).catch(() => { setTemplates([]); setErr("Could not load templates"); });
  }, []);
  const selected = (templates || []).find((t) => t.name === s.template_name && t.language === s.language);
  const varCount = selected?.var_count || 0;
  const chooseTemplate = (val) => {
    const [name, language] = val.split("||");
    set("template_name", name); set("language", language); set("var_map", []);
  };
  const setVar = (i, field) => {
    const vm = [...(s.var_map || [])]; vm[i] = field; set("var_map", vm);
  };
  return (
    <div className="space-y-3" data-testid="wa-template-picker">
      <div>
        <Label className="text-xs">Approved template</Label>
        {templates === null ? <div className="text-xs text-slate-400 py-1">Loading templates…</div> : (
          <select value={s.template_name ? `${s.template_name}||${s.language}` : ""} onChange={(e) => chooseTemplate(e.target.value)}
            className="w-full h-9 rounded-md border px-2 text-sm bg-transparent" data-testid="setting-template_name">
            <option value="">Select a template…</option>
            {templates.map((t) => <option key={`${t.name}||${t.language}`} value={`${t.name}||${t.language}`}>{t.name} ({t.language})</option>)}
          </select>
        )}
        {err && <div className="text-[11px] text-amber-600 mt-1">{err}</div>}
      </div>
      {selected?.body && <div className="text-[11px] text-slate-500 p-2 rounded bg-slate-50 dark:bg-slate-800">{selected.body}</div>}
      {varCount > 0 && (
        <div className="space-y-2">
          <Label className="text-xs">Map template variables to lead fields</Label>
          {Array.from({ length: varCount }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs w-10 text-slate-400">{`{{${i + 1}}}`}</span>
              <select value={(s.var_map || [])[i] || ""} onChange={(e) => setVar(i, e.target.value)}
                className="flex-1 h-8 rounded-md border px-2 text-xs bg-transparent" data-testid={`setting-var-${i}`}>
                <option value="">Select field…</option>
                {LEAD_FIELDS.map((f) => <option key={f.v} value={f.v}>{f.l}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
      {selected?.body && (
        <div data-testid="wa-template-preview">
          <Label className="text-xs text-slate-400 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Preview (sample lead)</Label>
          <div className="mt-1 rounded-lg rounded-tl-none bg-[#dcf8c6] dark:bg-emerald-900/40 text-slate-800 dark:text-emerald-50 p-2.5 text-xs whitespace-pre-wrap leading-relaxed shadow-sm">
            {fillTemplate(selected.body, s.var_map)}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Sample: {SAMPLE_LEAD.name} · {SAMPLE_LEAD.interest} · {SAMPLE_LEAD.city}</div>
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ node, onChange, onClose, onDeleteNode, wid }) {
  if (!node) return (
    <div className="w-72 shrink-0 border-l bg-white dark:bg-[#0f0d24] p-4 text-sm text-slate-400">
      <div className="font-semibold text-slate-600 dark:text-slate-300 mb-1">Block settings</div>
      Click any block on the canvas to edit its settings here.
    </div>
  );
  const schema = getSettingsSchema(node.data.type);
  const s = node.data.settings || {};
  const set = (k, v) => onChange(node.id, { label: node.data.label, settings: { ...s, [k]: v } });
  const hookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/wf/${wid}/trigger`;
  return (
    <div className="w-72 shrink-0 border-l bg-white dark:bg-[#0f0d24] p-4 overflow-y-auto" data-testid="settings-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold">Block settings</div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
      </div>
      {node.data.type === "trigger_webhook_received" && (
        <div className="mb-3 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-800" data-testid="webhook-trigger-url">
          <div className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 mb-1">Your webhook URL (POST leads here)</div>
          <code className="block text-[10px] break-all text-emerald-800 dark:text-emerald-200">{hookUrl}</code>
          <button className="mt-1.5 text-[11px] underline text-emerald-700 dark:text-emerald-300" onClick={() => { navigator.clipboard.writeText(hookUrl); toast.success("Webhook URL copied"); }}>Copy URL</button>
          <div className="text-[10px] text-emerald-600/80 mt-1">Body: {"{ mobile, name, interest, city, email }"}. Activate the workflow to receive.</div>
        </div>
      )}
      <div className="space-y-3">
        <div><Label className="text-xs">Block label</Label>
          <Input value={node.data.label} onChange={(e) => onChange(node.id, { label: e.target.value, settings: s })} data-testid="setting-label" /></div>
        {node.data.type === "action_wa_template" ? (
          <WaTemplatePicker s={s} set={set} />
        ) : schema.map((f) => (
          <div key={f.key}>
            <Label className="text-xs">{f.label}</Label>
            {f.type === "textarea" ? (
              <textarea value={s[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder}
                className="w-full min-h-[70px] rounded-md border px-2.5 py-1.5 text-sm bg-transparent" data-testid={`setting-${f.key}`} />
            ) : f.type === "select" ? (
              <select value={s[f.key] || ""} onChange={(e) => set(f.key, e.target.value)}
                className="w-full h-9 rounded-md border px-2 text-sm bg-transparent" data-testid={`setting-${f.key}`}>
                <option value="">Select…</option>
                {f.options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <Input type={f.type === "number" ? "number" : "text"} value={s[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`setting-${f.key}`} />
            )}
          </div>
        ))}
        <Button variant="outline" className="w-full text-red-600" onClick={() => onDeleteNode(node.id)} data-testid="delete-node-btn"><Trash2 className="w-3.5 h-3.5 mr-1" />Delete block</Button>
      </div>
    </div>
  );
}

function Builder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [name, setName] = useState("Untitled workflow");
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showTpl, setShowTpl] = useState(false);
  const wrapRef = useRef(null);
  const fileRef = useRef(null);
  const rf = useReactFlow();
  const hist = useRef({ past: [], future: [] });
  const dirty = useRef(false);

  useEffect(() => {
    api.get(`/workflows/${id}`).then((r) => {
      setName(r.data.name); setStatus(r.data.status || "draft");
      setNodes(r.data.nodes || []); setEdges(r.data.edges || []);
    }).catch(() => toast.error("Could not load workflow")).finally(() => setLoading(false));
  }, [id, setNodes, setEdges]);

  const snapshot = useCallback(() => { hist.current.past.push({ nodes: JSON.parse(JSON.stringify(nodes)), edges: JSON.parse(JSON.stringify(edges)) }); if (hist.current.past.length > 40) hist.current.past.shift(); hist.current.future = []; }, [nodes, edges]);

  const save = useCallback(async (silent) => {
    setSaving(true);
    try { await api.put(`/workflows/${id}`, { name, nodes, edges }); dirty.current = false; if (!silent) toast.success("Workflow saved"); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }, [id, name, nodes, edges]);

  // autosave every 10s if dirty
  useEffect(() => { dirty.current = true; }, [nodes, edges, name]);
  useEffect(() => { const t = setInterval(() => { if (dirty.current) save(true); }, 10000); return () => clearInterval(t); }, [save]);

  const onConnect = useCallback((params) => {
    const target = nodes.find((n) => n.id === params.target);
    if (target && target.data.kind === "trigger") { toast.error("Can't connect into a trigger block."); return; }
    if (params.source === params.target) return;
    snapshot();
    setEdges((eds) => addEdge({ ...params, markerEnd: { type: MarkerType.ArrowClosed }, animated: status === "active" }, eds));
  }, [nodes, setEdges, snapshot, status]);

  const isValidConnection = useCallback((c) => {
    const target = nodes.find((n) => n.id === c.target);
    return !!target && target.data.kind !== "trigger" && c.source !== c.target;
  }, [nodes]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const type = e.dataTransfer.getData("application/reactflow");
    if (!type) return;
    const kind = KIND_OF[type];
    const block = [...BLOCKS.trigger, ...BLOCKS.action, ...BLOCKS.condition].find((b) => b.type === type);
    const position = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
    snapshot();
    const newNode = { id: `n${Date.now()}`, type: "block", position, data: { type, label: block?.label || type, kind, settings: {} } };
    setNodes((nds) => nds.concat(newNode));
  }, [rf, setNodes, snapshot]);

  const onDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }, []);

  const updateNode = useCallback((nodeId, patch) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)));
  }, [setNodes]);

  const deleteNode = useCallback((nodeId) => {
    snapshot();
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((ed) => ed.source !== nodeId && ed.target !== nodeId));
    setSelectedId(null);
  }, [setNodes, setEdges, snapshot]);

  const undo = useCallback(() => { const h = hist.current; if (!h.past.length) return; h.future.push({ nodes, edges }); const prev = h.past.pop(); setNodes(prev.nodes); setEdges(prev.edges); }, [nodes, edges, setNodes, setEdges]);
  const redo = useCallback(() => { const h = hist.current; if (!h.future.length) return; h.past.push({ nodes, edges }); const nxt = h.future.pop(); setNodes(nxt.nodes); setEdges(nxt.edges); }, [nodes, edges, setNodes, setEdges]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "s") { e.preventDefault(); save(); }
      else if ((e.ctrlKey || e.metaKey) && e.key === "z") { e.preventDefault(); undo(); }
      else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || (e.shiftKey && e.key === "z"))) { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save, undo, redo]);

  const setWfStatus = async (st) => {
    try { await save(true); await api.post(`/workflows/${id}/status`, { status: st }); setStatus(st); setEdges((eds) => eds.map((e) => ({ ...e, animated: st === "active" }))); toast.success(st === "active" ? "Workflow activated" : "Workflow " + st); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const duplicate = async () => { try { const r = await api.post(`/workflows/${id}/duplicate`); toast.success("Duplicated"); nav(`/workflows/${r.data.id}`); } catch (e) { toast.error(apiErr(e)); } };
  const del = async () => { if (!window.confirm("Delete this workflow?")) return; try { await api.delete(`/workflows/${id}`); toast.success("Deleted"); nav("/workflows"); } catch (e) { toast.error(apiErr(e)); } };
  const exportJson = () => { const blob = new Blob([JSON.stringify({ name, nodes, edges }, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name.replace(/\s+/g, "_")}.json`; a.click(); };
  const importJson = (e) => { const f = e.target.files?.[0]; if (!f) return; const rd = new FileReader(); rd.onload = () => { try { const j = JSON.parse(rd.result); snapshot(); if (j.name) setName(j.name); setNodes(j.nodes || []); setEdges(j.edges || []); toast.success("Workflow imported"); } catch { toast.error("Invalid JSON file"); } }; rd.readAsText(f); e.target.value = ""; };
  const loadTemplate = (tpl) => { snapshot(); const { nodes: n, edges: ed } = tpl.build(); setName(tpl.name); setNodes(n); setEdges(ed.map((x) => ({ ...x, markerEnd: { type: MarkerType.ArrowClosed } }))); setShowTpl(false); setTimeout(() => rf.fitView({ padding: 0.2 }), 60); toast.success(`Loaded "${tpl.name}" template`); };

  const selected = useMemo(() => nodes.find((n) => n.id === selectedId) || null, [nodes, selectedId]);
  const statusBadge = { active: { l: "Active", c: "#16a34a", bg: "#dcfce7" }, inactive: { l: "Inactive", c: "#64748b", bg: "#f1f5f9" }, draft: { l: "Draft", c: "#2563eb", bg: "#dbeafe" } }[status] || {};

  if (loading) return <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;

  return (
    <div className="flex flex-col h-screen" data-testid="workflow-builder">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b bg-white dark:bg-[#0f0d24]">
        <Button variant="ghost" size="sm" onClick={() => nav("/workflows")} data-testid="wf-back"><ArrowLeft className="w-4 h-4" /></Button>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="w-56 h-9 font-semibold" data-testid="wf-name" />
        <span className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ color: statusBadge.c, background: statusBadge.bg }} data-testid="wf-status">{statusBadge.l}</span>
        <div className="w-px h-6 bg-slate-200 mx-1" />
        <Button size="sm" variant="outline" onClick={() => save()} disabled={saving} data-testid="wf-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save</>}</Button>
        <Button size="sm" variant="outline" onClick={() => setShowTpl((v) => !v)} data-testid="wf-templates"><LayoutTemplate className="w-4 h-4 mr-1" />Templates</Button>
        <Button size="sm" variant="outline" onClick={duplicate} data-testid="wf-duplicate"><Copy className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={del} className="text-red-600" data-testid="wf-delete"><Trash2 className="w-4 h-4" /></Button>
        <div className="w-px h-6 bg-slate-200 mx-1" />
        <Button size="sm" variant="outline" onClick={undo} title="Undo (Ctrl+Z)"><Undo2 className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={redo} title="Redo (Ctrl+Y)"><Redo2 className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => rf.fitView({ padding: 0.2 })} title="Fit to screen"><Maximize2 className="w-4 h-4" /></Button>
        <div className="w-px h-6 bg-slate-200 mx-1" />
        <Button size="sm" variant="outline" onClick={exportJson} title="Export JSON"><Download className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} title="Import JSON"><Upload className="w-4 h-4" /></Button>
        <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={importJson} />
        <div className="flex-1" />
        {status === "active"
          ? <Button size="sm" variant="outline" onClick={() => setWfStatus("inactive")} data-testid="wf-deactivate"><Power className="w-4 h-4 mr-1" />Deactivate</Button>
          : <Button size="sm" style={{ background: "#16a34a" }} onClick={() => setWfStatus("active")} data-testid="wf-activate"><Play className="w-4 h-4 mr-1" />Activate</Button>}
      </div>

      <div className="flex-1 flex overflow-hidden relative">
        <Palette />
        {showTpl && (
          <div className="absolute left-56 top-2 z-20 w-72 bg-white dark:bg-[#15122e] border rounded-xl shadow-lg p-2" data-testid="template-menu">
            <div className="text-xs font-semibold px-1 py-1 text-slate-500">Load a template (replaces canvas)</div>
            {TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => loadTemplate(t)} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-white/5" data-testid={`tpl-${t.id}`}>
                <div className="text-sm font-medium">{t.name}</div><div className="text-[11px] text-slate-400">{t.desc}</div>
              </button>
            ))}
          </div>
        )}
        <div ref={wrapRef} className="flex-1 h-full" onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            isValidConnection={isValidConnection}
            onNodeClick={(_, n) => setSelectedId(n.id)} onPaneClick={() => setSelectedId(null)}
            onNodeDragStart={snapshot}
            deleteKeyCode={["Delete", "Backspace"]} multiSelectionKeyCode={["Shift"]}
            fitView proOptions={{ hideAttribution: true }} defaultEdgeOptions={{ markerEnd: { type: MarkerType.ArrowClosed } }}>
            <Background variant={BackgroundVariant.Dots} gap={18} size={1} />
            <Controls />
            <MiniMap pannable zoomable nodeColor={(n) => (KIND_COLOR[n.data?.kind] || KIND_COLOR.action).dot} />
          </ReactFlow>
        </div>
        <SettingsPanel node={selected} onChange={updateNode} onClose={() => setSelectedId(null)} onDeleteNode={deleteNode} wid={id} />
      </div>
    </div>
  );
}

export default function WorkflowBuilder() {
  return <ReactFlowProvider><Builder /></ReactFlowProvider>;
}
